import React, { useEffect, useState, useRef, Suspense, lazy, memo } from 'react';
import { ProgressBar } from './components/ProgressBar';
import { Hero } from './components/Hero';
import { IntroNarrative } from './components/IntroNarrative';
import { InteractiveTOC } from './components/InteractiveTOC';
// Lazy load components that appear below the fold
const Reason1 = lazy(() => import('./components/Reason1').then(module => ({
  default: module.Reason1
})));
const Reason2 = lazy(() => import('./components/Reason2').then(module => ({
  default: module.Reason2
})));
const Reason3 = lazy(() => import('./components/Reason3').then(module => ({
  default: module.Reason3
})));
const Reason4 = lazy(() => import('./components/Reason4').then(module => ({
  default: module.Reason4
})));
const Reason5 = lazy(() => import('./components/Reason5').then(module => ({
  default: module.Reason5
})));
const FinalCTA = lazy(() => import('./components/FinalCTA').then(module => ({
  default: module.FinalCTA
})));
const PSSection = lazy(() => import('./components/PSSection').then(module => ({
  default: module.PSSection
})));
const StickyCTA = lazy(() => import('./components/StickyCTA').then(module => ({
  default: module.StickyCTA
})));
const Footer = lazy(() => import('./components/Footer').then(module => ({
  default: module.Footer
})));
const FloatingChat = lazy(() => import('./components/FloatingChat').then(module => ({
  default: module.FloatingChat
})));
// Defer analytics until after critical content loads - use dynamic import for better code splitting
const MetaPixel = lazy(() => import('./components/MetaPixel').then(module => ({
  default: module.MetaPixel
})));
const GoogleAnalytics = lazy(() => import('./components/GoogleAnalytics').then(module => ({
  default: module.GoogleAnalytics
})));
const GoogleTagManager = lazy(() => import('./components/GoogleTagManager').then(module => ({
  default: module.GoogleTagManager
})));
const ClickTracker = lazy(() => import('./components/ClickTracker').then(module => ({
  default: module.ClickTracker
})));
export function App() {
  const [scrollProgress, setScrollProgress] = useState(0);
  const [activeSection, setActiveSection] = useState('');
  const [showStickyCTA, setShowStickyCTA] = useState(false);
  const [mousePosition, setMousePosition] = useState({
    x: 0,
    y: 0
  });
  // Use refs to optimize scroll and animation handling
  const ticking = useRef(false);
  const sections = useRef([]);
  const parallaxElements = useRef([]);
  const animatedElements = useRef([]);
  const observer = useRef(null);
  const scrollListenerAdded = useRef(false);
  const mouseListenerAdded = useRef(false);
  // Optimization: Use a separate effect for the IntersectionObserver to avoid recreating it
  useEffect(() => {
    // Set up IntersectionObserver for animations with optimized options
    observer.current = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('active');
          // Optimization: Unobserve elements after they've been activated to reduce overhead
          observer.current.unobserve(entry.target);
        }
      });
    }, {
      rootMargin: '0px 0px -10% 0px',
      threshold: 0.1
    });
    return () => {
      if (observer.current) {
        observer.current.disconnect();
      }
    };
  }, []);
  // Optimization: Separate effect for DOM queries to reduce work on mount
  useEffect(() => {
    // Use requestIdleCallback to defer non-critical initialization
    const idleCallback = window.requestIdleCallback || (cb => setTimeout(cb, 50));
    idleCallback(() => {
      // Cache DOM elements for better performance - do this once instead of every scroll
      sections.current = document.querySelectorAll('section[id^="reason-"]');
      parallaxElements.current = document.querySelectorAll('.parallax');
      animatedElements.current = document.querySelectorAll('.reveal, .reveal-up, .reveal-left, .reveal-right, .reveal-scale, .reveal-rotate, .reveal-blur, .animate-stagger-fade-in');
      // Observe all animated elements
      animatedElements.current.forEach(el => observer.current.observe(el));
    });
    return () => {
      if (observer.current) {
        animatedElements.current.forEach(el => observer.current.unobserve(el));
      }
    };
  }, []);
  // Optimization: Separate effect for scroll handling to avoid unnecessary re-creation
  useEffect(() => {
    if (scrollListenerAdded.current) return;
    // Optimized scroll handler with requestAnimationFrame and throttling
    const handleScroll = () => {
      if (!ticking.current) {
        ticking.current = true;
        requestAnimationFrame(() => {
          // Calculate scroll progress
          const totalHeight = document.body.scrollHeight - window.innerHeight;
          const progress = Math.min(100, Math.max(0, window.scrollY / totalHeight * 100));
          setScrollProgress(progress);
          // Show sticky CTA after 30% scroll - use memo to avoid unnecessary state updates
          if (progress > 30 && !showStickyCTA || progress <= 30 && showStickyCTA) {
            setShowStickyCTA(progress > 30);
          }
          // Determine active section for TOC - only update state if it changes
          let newActiveSection = activeSection;
          for (let i = 0; i < sections.current.length; i++) {
            const section = sections.current[i];
            const rect = section.getBoundingClientRect();
            const sectionVisiblePercent = Math.min(Math.max(0, (window.innerHeight - rect.top) / rect.height), 1) * 100;
            if (sectionVisiblePercent > 70) {
              newActiveSection = section.id;
              break;
            }
          }
          if (newActiveSection !== activeSection) {
            setActiveSection(newActiveSection);
          }
          ticking.current = false;
        });
      }
    };
    // Add event listener with passive flag for better performance
    window.addEventListener('scroll', handleScroll, {
      passive: true
    });
    scrollListenerAdded.current = true;
    // Initial call to handle elements already in view
    handleScroll();
    return () => {
      window.removeEventListener('scroll', handleScroll);
      scrollListenerAdded.current = false;
    };
  }, [activeSection, showStickyCTA]);
  // Optimization: Separate effect for mouse movement to avoid unnecessary re-creation
  useEffect(() => {
    if (mouseListenerAdded.current) return;
    // Optimized mouse move handler with throttling
    let lastMouseMoveTime = 0;
    const throttleTime = 16; // ~60fps
    const handleMouseMove = e => {
      const now = performance.now();
      if (now - lastMouseMoveTime < throttleTime) return;
      lastMouseMoveTime = now;
      // Only update state if we have parallax elements
      if (parallaxElements.current.length > 0) {
        setMousePosition({
          x: e.clientX,
          y: e.clientY
        });
        // Apply parallax effect with optimized calculations
        const centerX = window.innerWidth / 2;
        const centerY = window.innerHeight / 2;
        parallaxElements.current.forEach(element => {
          const depth = parseFloat(element.getAttribute('data-depth') || '0.05');
          const moveX = (e.clientX - centerX) * depth;
          const moveY = (e.clientY - centerY) * depth;
          // Use transform for better performance
          element.style.transform = `translate3d(${moveX}px, ${moveY}px, 0)`;
        });
      }
    };
    // Add event listener with passive flag for better performance
    window.addEventListener('mousemove', handleMouseMove, {
      passive: true
    });
    mouseListenerAdded.current = true;
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      mouseListenerAdded.current = false;
    };
  }, []);
  // Fallback loading state for lazy-loaded components
  const fallbackStyle = {
    minHeight: '20px',
    width: '100%',
    background: 'transparent'
  };
  return <div className="bg-slate-50 min-h-screen w-full font-sans text-slate-800" data-id="element-0">
      {/* Defer non-critical analytics loading */}
      <Suspense fallback={null} data-id="element-1">
        <GoogleTagManager id="GTM-MLNB6CQZ" data-id="element-2" />
        <MetaPixel data-id="element-3" />
        <GoogleAnalytics data-id="element-4" />
        <ClickTracker data-id="element-5" />
      </Suspense>
      <ProgressBar progress={scrollProgress} data-id="element-6" />
      <main data-id="element-7">
        <Hero data-id="element-8" />
        <div className="max-w-6xl mx-auto px-4 md:px-6 lg:px-8 pb-16" data-id="element-9">
          <div className="flex flex-col lg:flex-row lg:gap-12" data-id="element-10">
            <div className="lg:w-3/4" data-id="element-11">
              <IntroNarrative data-id="element-12" />
              <section id="reason-1" className="reveal-scale" data-id="element-13">
                <Suspense fallback={<div style={fallbackStyle} data-id="element-15"></div>} data-id="element-14">
                  <Reason1 data-id="element-16" />
                </Suspense>
              </section>
              <section id="reason-2" className="reveal-scale" data-id="element-17">
                <Suspense fallback={<div style={fallbackStyle} data-id="element-19"></div>} data-id="element-18">
                  <Reason2 data-id="element-20" />
                </Suspense>
              </section>
              <section id="reason-3" className="reveal-scale" data-id="element-21">
                <Suspense fallback={<div style={fallbackStyle} data-id="element-23"></div>} data-id="element-22">
                  <Reason3 data-id="element-24" />
                </Suspense>
              </section>
              <section id="reason-4" className="reveal-scale" data-id="element-25">
                <Suspense fallback={<div style={fallbackStyle} data-id="element-27"></div>} data-id="element-26">
                  <Reason4 data-id="element-28" />
                </Suspense>
              </section>
              <section id="reason-5" className="reveal-scale" data-id="element-29">
                <Suspense fallback={<div style={fallbackStyle} data-id="element-31"></div>} data-id="element-30">
                  <Reason5 data-id="element-32" />
                </Suspense>
              </section>
              <Suspense fallback={<div style={fallbackStyle} data-id="element-34"></div>} data-id="element-33">
                <FinalCTA data-id="element-35" />
              </Suspense>
            </div>
            <div className="hidden lg:block lg:w-1/4 relative" data-id="element-36">
              <div className="sticky top-6" data-id="element-37">
                <InteractiveTOC activeSection={activeSection} data-id="element-38" />
              </div>
            </div>
          </div>
        </div>
      </main>
      {showStickyCTA && <Suspense fallback={null} data-id="element-39">
          <StickyCTA data-id="element-40" />
        </Suspense>}
      <Suspense fallback={null} data-id="element-41">
        <FloatingChat data-id="element-42" />
      </Suspense>
      <Suspense fallback={null} data-id="element-43">
        <Footer data-id="element-44" />
      </Suspense>
    </div>;
}